import React, { useEffect, useRef, useState } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
  deleteItem,
  insertItem,
  updateItem,
  getFieldTypeId,
} from "./form-automation-util";
import { CommandCell } from "./command-cell";
import "./form-automation-grid.scss";
import { DropDownCell } from "./dropdown-cell";
import { DropDownCellTable } from "./dropdown-cell-table";
import { DropDownCellColumn } from "./dropdown-cell-column";
import { DropDownCellProperty } from "./dropdown-cell-property";
import { PropertyValueCell } from "./dropdown-cell-property-value";
import { TextboxCell } from "./textbox-cell";
import { filterBy } from "@progress/kendo-data-query";
import FluentUiIcon from "../../../../../../../assets/fluent-ui-icon";
import { FluentButton } from "../../../../../../../components/FluentUI/Fluent-Button";
import {
  GetWorkflowFormFieldProperties,
  UpdateWorkflowFormFieldProperties,
} from "../../../../../../../services/api/task-types-api";
import { PulseLoader } from "react-spinners";
import PropTypes from "prop-types";

const editField = "inEdit";
const list = {
  DynamicFieldColumns: {},
  DynamicForms: {},
  DynamicFields: {},
  // prettier-ignore
  Visible:{true:"Yes",false:"No"},
  // prettier-ignore
  Disabled:{true:"Yes",false:"No"},
  CustomProperties: [],
  CustomFormulas: [],
  CustomPropertyValues: [],
};
const initialFilter = {
  logic: "and",
  filters: [],
};

export const FormAutomationGrid = ({
  formId,
  formName,
  setFormId,
  type,
  name,
}) => {
  const [conditions, setConditions] = useState({
    isLoading: true,
    data: [],
  });
  const currentRowRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [filter, setFilter] = useState(initialFilter);
  const [tableName, setTableName] = useState("");

  const getWorkflowFormFieldProperties = async () => {
    const response = await GetWorkflowFormFieldProperties(formId, type);

    setConditions((prevState) => ({
      ...prevState,
      data: response?.data?.WorkflowFormFieldProperties,
      isLoading: false,
    }));

    list.DynamicFieldColumns = response?.data?.DynamicFieldColumns;
    list.DynamicForms = response?.data?.DynamicForms;
    list.DynamicFields = response?.data?.DynamicFields;
    list.CustomProperties = response?.data?.CustomProperties;
    list.CustomFormulas = response?.data?.CustomFormulas;
    list.CustomPropertyValues = response?.data?.CustomPropertyValues;
  };

  const ReCreateDropdownProperty = (props) => {
    return React.createElement(DropDownCellProperty, { ...props, list });
  };

  const ReCreateDropdownPropertyValue = (props) => React.createElement(PropertyValueCell, { ...props, list });

  const updateWorkflowFormFieldProperties = async (updatedData) => {
    const response = await UpdateWorkflowFormFieldProperties(
      formId,
      updatedData,
      type
    );

    if (response.status === 200) {
      getWorkflowFormFieldProperties();
    }
  };

  useEffect(() => {
    getWorkflowFormFieldProperties();
  }, []);

  const ReCreateDropdown = (props) => {
    return React.createElement(DropDownCell, { ...props, list });
  };

  const ReCreateDropdownTables = (props) => {
    return React.createElement(DropDownCellTable, {
      ...props,
      list,
      setTableName,
    });
  };

  const ReCreateDropdownColumns = (props) => {
    return React.createElement(DropDownCellColumn, {
      ...props,
      list,
      tableName,
    });
  };

  const ReCreateTextbox = (props) => {
    return React.createElement(TextboxCell, { ...props, list });
  };

  const remove = (dataItem) => {
    const newData = JSON.parse(JSON.stringify(conditions?.data));
    const updatedData = [...deleteItem(newData, dataItem)];
    currentRowRef.current = null;

    updateWorkflowFormFieldProperties(updatedData);
  };

  const add = () => {
    const newData = JSON.parse(JSON.stringify(conditions?.data));
    const updatedData = insertItem(newData, currentRowRef.current);
    currentRowRef.current = null;

    updateWorkflowFormFieldProperties(updatedData);
  };

  const update = () => {
    const newData = JSON.parse(JSON.stringify(conditions?.data));
    const updatedData = updateItem(newData, currentRowRef.current);
    currentRowRef.current = null;

    updateWorkflowFormFieldProperties(updatedData);
  };

  const discard = () => {
    const newData = JSON.parse(JSON.stringify(conditions?.data));
    newData.shift();
    currentRowRef.current = null;

    setConditions((prevState) => ({
      ...prevState,
      data: newData,
    }));
  };

  const cancel = (dataItem) => {
    const newData = JSON.parse(JSON.stringify(conditions?.data));
    const originalItem = newData?.find((p) => p.Id === dataItem.Id);
    originalItem.inEdit = false;
    currentRowRef.current = null;

    setConditions((prevState) => ({
      ...prevState,
      data: newData,
    }));
  };

  const enterEdit = (dataItem) => {
    const newData = JSON.parse(JSON.stringify(conditions?.data));
    const originalItem = newData?.find((p) => p.Id === dataItem.Id);
    originalItem.inEdit = true;

    currentRowRef.current = JSON.parse(JSON.stringify(originalItem));

    setConditions((prevState) => ({
      ...prevState,
      data: newData,
    }));
  };

  const itemChange = (event) => {
    if (event.value === null) return;
    if (typeof event?.value === "object") {
      if (event.field === "IsVisible" || event.field === "IsDisabled") {
        currentRowRef.current[event.field] = event?.value?.id === "true";
      } else {
        currentRowRef.current[event.field] = event?.value?.text;
        currentRowRef.current[getFieldTypeId(event.field)] = event?.value?.id;
      }
    } else {
      currentRowRef.current[event.field] = event.value;
    }
  };

  const addNew = () => {
    if (currentRowRef.current === null) {
      const newDataItem = {
        inEdit: true,
        Id: null,
        DynamicFieldColumnId: null,
        DynamicFieldColumnName: null,
        DynamicFormId: null,
        DynamicFormName: null,
        DynamicFieldId: null,
        DynamicFieldName: null,
        CustomProperty: null,
        CustomFormula: null,
        CustomPropertyValue: null,
        IsVisible: null,
        IsDisabled: null,
      };
      currentRowRef.current = JSON.parse(JSON.stringify(newDataItem));

      setConditions((prevState) => ({
        ...prevState,
        data: [newDataItem, ...prevState.data],
      }));
    }
  };

  return !conditions?.isLoading ? (
    <div className={isFullScreen ? "fullscreen-grid" : ""}>
      <div className={isFullScreen ? "mt-2 top-actions" : "top-actions"}>
        <div className="title text-left mt-2 mb-2">{name}</div>
        <div className="bottom">
          <FluentButton
            icon={
              <FluentUiIcon
                name={
                  isFullScreen
                    ? "FullScreenMinimizeFilled"
                    : "FullScreenMaximizeFilled"
                }
                color={"#212121"}
                size={18}
              />
            }
            onClick={() => {
              setIsFullScreen((fc) => !fc);
            }}
          />
          <FluentButton
            icon={
              <FluentUiIcon name={"AddRegular"} color={"#212121"} size={20} />
            }
            onClick={addNew}
            text={"Add New"}
          />
        </div>
      </div>
      <div className="form-automation-grid">
        <Grid
          onItemChange={itemChange}
          editField={editField}
          data={filterBy(conditions?.data, filter)}
          navigatable={true}
          filterable={true}
          filter={filter}
          onFilterChange={(e) => setFilter(e.filter)}
          resizable={true}
        >
          {/* <Column
            field="DynamicFormName"
            title="Form"
            cell={ReCreateDropdown}
          /> */}
          {type !== "form-visibility" && (
            <Column
              field="DynamicFieldName"
              title={type === "table" ? "Table" : "Field"}
              cell={
                type === "table" ? ReCreateDropdownTables : ReCreateDropdown
              }
            />
          )}
          {type !== "form-visibility" && (
            <Column
              field="DynamicFieldColumnName"
              title="Table Column"
              cell={ReCreateDropdownColumns}
            />
          )}
          <Column
            field="CustomProperty"
            title="Property"
            cell={ReCreateDropdownProperty}
          />

          {type === "field-visibility" && (
            <Column
              field="CustomFormula"
              title="Formula"
              cell={ReCreateTextbox}
            />
          )}
          <Column
            field="CustomPropertyValue"
            title="Property Value"
            cell={ReCreateDropdownPropertyValue}
          />
          <Column
            field="IsVisible"
            title="Visible"
            filterable={false}
            cell={ReCreateDropdown}
          />
          {type !== "field-visibility" && (
            <Column
              field="IsDisabled"
              title="Disabled"
              filterable={false}
              cell={ReCreateDropdown}
            />
          )}
          <Column
            filterable={false}
            cell={(props) => (
              <CommandCell
                {...props}
                {...{
                  edit: enterEdit,
                  remove: remove,
                  add: add,
                  discard: discard,
                  update: update,
                  cancel: cancel,
                  editField: editField,
                }}
              />
            )}
          />
        </Grid>
      </div>
    </div>
  ) : (
    <div className="pulse-loader" data-testid="loading-spinner">
      <PulseLoader/>
    </div>
  );
};

FormAutomationGrid.propTypes = {
  formId: PropTypes.number,
  forName: PropTypes.string,
  setFormId: PropTypes.func,
  type: PropTypes.string,
  name: PropTypes.string,
};
